/**
 * Rio's PLAJ ŞEMSİYESİ — the beach parasol — in the shared 120×120 LINE box.
 *
 * The passcode dots' glyph, and the owner's call twice over (2026-08-14: first
 * "passcode isa olur mu saçma. oraya shezlong falan olabilir tatil modu", then
 * "bari plaj şemsiyesi yap iki renk sarı yeşil"). Cristo Redentor keeps the Home
 * TAB, where it appears once and reads as a landmark; four of it across a lock
 * screen changes the register of the whole thing.
 *
 * The lounger that came between them is still in the tree (line-lounger-glyph)
 * and it lost for a reason worth keeping: a chair in side elevation is all thin
 * bars, and at the ~30px LINE renders a passcode dot at, thin bars are a smear.
 * A parasol is one big shape with a stick under it, which is the kind of thing
 * that survives being shrunk.
 *
 * WHAT IS LOAD-BEARING:
 *
 *  - SIX PANELS, ALTERNATING, and the OUTER TWO ARE GREEN. Alternating panels
 *    are the whole idea of a beach parasol and they are also what separates this
 *    from a mushroom. Green on the outside because the yellow is `#E6C41A` and
 *    measures 1.6:1 on this skin's white passcode zone — a canopy edged in yellow
 *    has no edge at all, and the green rim at 3.3:1 is what draws the shape.
 *  - The RIM SAGS. Ten units lower in the middle than at the ends, plus a scallop
 *    per panel. A straight rim is an umbrella held over a person; a sagging,
 *    scalloped one is planted in sand.
 *  - The POLE IS OFF-CENTRE AND LEANS. A beach parasol is pushed into the sand at
 *    an angle, and the lean is the difference between a holiday and a patio.
 *
 * Every subpath is wound clockwise so a nonzero fill unions the overlaps rather
 * than punching holes ([[line-shared-glyph-modules]]) — checked by `cw()` rather
 * than by eye, since a panel's winding flips with the direction it is walked.
 *
 * The fit: ink spans y 18 → 106 and x 14 → 106.
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/* ---------------------------------------------------------------- geometry -- */

/** The top of the pole, which is also where every panel meets. */
const APEX_X = 60, APEX_Y = 30;
/** The canopy's span and how far its rim sags at the middle. Flatter and wider
 *  than the first pass (apex 20 → 30, rim 52 → 56, span 92 → 84): the owner asked
 *  for it more OPEN ("biraz daha açık olsunlar"), and an open parasol is a
 *  shallow dome — the steep one read as a closed umbrella still in its sleeve. */
const RIM_L = 18, RIM_R = 102, RIM_Y = 56, SAG = 11;
const PANELS = 6;

/** Where the pole enters the sand. Every tilt turns about this point, because
 *  that is what a parasol pushed into a beach actually does. */
export const PARASOL_PIVOT: [ number, number ] = [ 63, 100 ];

/** A point on the rim, t running 0..1 left to right. */
function rim( t: number ): [ number, number ] {
  return [ RIM_L + ( RIM_R - RIM_L ) * t, RIM_Y + Math.sin( Math.PI * t ) * SAG ];
}

/* --------------------------------------------------------------- the parts -- */

/**
 * The panels, left to right. Each is the apex plus one rim segment, and the rim
 * segment BULGES DOWN — that bulge is the scallop, and it is what stops six
 * triangles from reading as a folded fan.
 *
 * They overlap by a fifth of a unit at every seam, because a hairline of white
 * between two panels is the first thing to appear when the whole thing is scaled
 * to a third of its size.
 */
function panel( i: number ): string {
  const a = rim( i / PANELS ), b = rim( ( i + 1 ) / PANELS );
  const mid: [ number, number ] = [ ( a[ 0 ] + b[ 0 ] ) / 2, ( a[ 1 ] + b[ 1 ] ) / 2 + 5 ];
  // Walked apex → RIGHT rim → left rim, which is CLOCKWISE. The first pass walked
  // it the other way, and that is what put a white slot through every OFF
  // parasol: the flat silhouette unions the panels with the pole, the pole comes
  // out of cw() clockwise, and two opposite windings under a nonzero fill punch a
  // hole exactly where they overlap (owner 2026-08-14: "şemsiye içine giren çizgi
  // beyaz olmuş").
  return `M${ APEX_X },${ APEX_Y } L${ f( b[ 0 ] + 0.2 ) },${ f( b[ 1 ] ) }`
    + ` Q${ f( mid[ 0 ] ) },${ f( mid[ 1 ] ) } ${ f( a[ 0 ] - 0.2 ) },${ f( a[ 1 ] ) } Z`;
}

/** The three GREEN panels: the two outer ones and the middle one. */
export const PARASOL_PANELS_A = [ panel( 0 ), panel( 2 ), panel( 5 ) ].join( ' ' );
/** The three YELLOW panels between them. */
export const PARASOL_PANELS_B = [ panel( 1 ), panel( 3 ), panel( 4 ) ].join( ' ' );

/** The pole, from just under the apex down into the sand, plus a stub of finial
 *  above the canopy so the apex does not end in a point. Both clockwise, like the
 *  panels. */
export const PARASOL_POLE = cw( [
  [ APEX_X - 2.6, APEX_Y + 2 ], [ APEX_X + 2.6, APEX_Y + 2 ],
  [ PARASOL_PIVOT[ 0 ] + 2.6, PARASOL_PIVOT[ 1 ] ], [ PARASOL_PIVOT[ 0 ] - 2.6, PARASOL_PIVOT[ 1 ] ],
] ) + ' ' + cw( [
  [ APEX_X - 2, APEX_Y - 7 ], [ APEX_X + 2, APEX_Y - 7 ],
  [ APEX_X + 2, APEX_Y + 3 ], [ APEX_X - 2, APEX_Y + 3 ],
] );

/**
 * The whole parasol as ONE path, for the OFF state.
 *
 * It is the SAME six panels the ON state draws plus the same pole, not a
 * separate dome drawn to look like them. That matters: a hand-written silhouette
 * drifts from the panels the moment either is tuned, and it was also the thing
 * whose winding disagreed with the pole's and cut a white line through the
 * canopy.
 */
export const PARASOL_SOLID = [
  PARASOL_PANELS_A, PARASOL_PANELS_B, PARASOL_POLE,
].join( ' ' );

/**
 * The four dots' POSES: a lean in degrees about PARASOL_PIVOT, and the size the
 * parasol is drawn at.
 *
 * This started as tilts alone, with the size derived from the tilt, and it went
 * through the owner three times (2026-08-14): lean them like one planted in front
 * of a lounger ("biraz yatay olsunlar hani şezlong önüne konmuş gibi"); then not
 * all the same ("açıları farklı olsun"); then not all leaning LEFT, with the
 * second and fourth brought back up ("hepsi sola yatık oldu ... 2. ve 4. çok
 * yatık"); then the third one up to their size ("3. şemsiye daha büyük olsun 2 ve
 * 4 gibi"); and finally all four distinct ("bide hepsi farklı olsun").
 *
 * That last ask is what killed the derived size. A size computed from the lean
 * can only ever say "leans more, drawn smaller", so the moment two dots want
 * different angles AND comparable sizes the rule is in the way. The pairs are
 * authored now, which is also why they are here rather than in the component: one
 * table, four rows, nothing to keep in step.
 *
 * The first one was pulled back to 9 degrees on top of all that ("1. çok yatık
 * oldu biraz daha dik olsun"), which also makes it the one nearly-upright parasol
 * in the row — worth having, since four leaners with nothing standing straight
 * read as a windy day rather than as a beach.
 *
 * Every pose is measured to stay inside the 120 box — the widest swing, the
 * 24-degree one at 0.95, puts its far rim at x 7.1.
 */
export const PARASOL_POSES: { a: number; k: number }[] = [
  { a: -9, k: 0.93 },
  { a: 12, k: 0.97 },
  { a: -24, k: 0.95 },
  { a: 14, k: 1.0 },
];

/**
 * The Home TAB's pose (owner 2026-08-14: "şimdi home da şemsiye olacak").
 *
 * It leans less than any of the passcode's. A tab glyph is read at a glance
 * beside four other icons, and the lean that gives a lock screen its holiday
 * reads as a wonky icon up there.
 *
 * And it is drawn OVER size, 1.18 (owner: "home şemsiyeleri daha büyük olsun").
 * The reason it needed saying is that this glyph's ink only spans x 18..102 and
 * y 23..100 of the 120 box, so at 1.0 it measures smaller than the Phosphor icons
 * beside it even though the boxes match — the trap [[line-home-glyph-equal-size]]
 * records, where a glyph has to be sized against a rendered neighbour rather than
 * against its own viewBox. Measured at 1.18 and 9 degrees the far rim lands at
 * x 2.5 and the finial at y 10.7, both still inside.
 */
export const PARASOL_TAB_POSE = { a: -9, k: 1.18 };

/* ------------------------------------------------------- passcode colours --
 * Held here, not in the passcode component: line-lock (the preview) and
 * line-page (the exported PNGs) both read them, and every time this family kept
 * two copies of a colour they drifted.
 */

/** The green panels and the pole. Emerald Palm, 3.32:1 on the skin's white
 *  passcode zone — the only tone in this pair that draws the shape's edge. */
export const PARASOL_GREEN = '#028134';
/** The yellow ones. Carnival Gold: 1.6:1 on white, which is fine INSIDE a shape
 *  whose outline is carried by the green, and would not be if it were the rim. */
export const PARASOL_YELLOW = '#E6C41A';

/** Guanabara Blue, the theme's colour circle. On the white passcode zone it
 *  measures 3.82:1 — the strongest edge in this theme's circles. */
export const PARASOL_BLUE = '#2287D5';

/**
 * The OUTER panels and pole of each of the four dots, in order.
 *
 * They alternate green, blue, green, blue (owner 2026-08-14: "passcode'da da 2.
 * ve 4 mavi sarı olsun"), and the inner panels stay yellow throughout — so every
 * dot is still a two-colour parasol and the row has two pairs in it, the same two
 * the chat wallpaper carries.
 *
 * The yellow is deliberately the INNER half in both pairs: at 1.6:1 on white it
 * cannot draw an edge, and a canopy whose rim is yellow has no rim.
 */
export const PARASOL_PC_COLORS = [
  PARASOL_GREEN, PARASOL_BLUE, PARASOL_GREEN, PARASOL_BLUE,
];

/** ONE tone for every unentered parasol, canopy and pole alike. Garden Path at
 *  1.41:1 on the white: plainly there, plainly closed for the day. */
export const PARASOL_PC_OFF = '#B8E5AD';
